import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Save, Loader2, Check, Palette, ChevronDown, Calendar, Clock, MapPin, MessageCircle, Music, Image as ImageIcon, Type as TypeIcon, User } from 'lucide-react';
import { templates, extraOptions, pricingTiers, addToCart } from '../mock';
import MusicPicker from '../components/MusicPicker';
import ImageUploader from '../components/ImageUploader';
import StaticPreview from '../components/StaticPreview';
import { useTranslation } from '../LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BuilderPage() {
  const { t, price } = useTranslation();
  const navigate = useNavigate();

  const [tierId, setTierId] = useState('base');
  const [tpl, setTpl] = useState(templates[0]);
  const [palette, setPalette] = useState(templates[0].palette);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [form, setForm] = useState({
    eventName: 'Il compleanno di Sofia',
    celebrant: 'Sofia',
    age: '30',
    date: '2026-09-14',
    time: '18:30',
    location: 'Villa dei Fiori, Roma',
    program: 'Aperitivo dalle 18:30 • Cena ore 20:00 • Torta e balli a seguire',
    message: 'Ti aspettiamo per festeggiare insieme',
    dress: 'Elegante',
    fontStyle: 'serif',
    music: templates[0].music,
    customPhotoUrl: '',
    whatsappNumber: '',
    extras: [],
    // Extra option configs
    extraRegistryUrl: '',
    extraMenu: '',
    extraCustomMusicUrl: '',
    extraVideoUrl: '',
    extraGalleryUrls: [],
    extraDressCode: '',
  });
  const [saving, setSaving] = useState(false);

  const tier = pricingTiers.find((x) => x.id === tierId);
  const hasPlus = tierId === 'plus' || tierId === 'complete';
  const hasComplete = tierId === 'complete';

  const pickTpl = (tt) => {
    setTpl(tt);
    setPalette(tt.palette);
    setSelectedTrack(null);
    setForm((f) => ({ ...f, music: tt.music }));
  };

  const toggleExtra = (id) => setForm((f) => ({
    ...f,
    extras: f.extras.includes(id) ? f.extras.filter((x) => x !== id) : [...f.extras, id],
  }));

  const previewData = {
    ...form,
    template_name: tpl.name,
    name: tpl.name,
    image: form.customPhotoUrl || tpl.image,
    palette,
    event: form.eventName,
    event_name: form.eventName,
    font_style: form.fontStyle,
  };

  const extrasPrice = form.extras.length * 0.5;
  const total = (tier?.price || 0) + extrasPrice;

  const handleSaveAndCart = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/invitations`, {
        template_id: tpl.id,
        template_name: tpl.name,
        palette,
        eventName: form.eventName,
        celebrant: form.celebrant,
        age: form.age,
        date: form.date,
        time: form.time,
        location: form.location,
        program: form.program,
        message: form.message,
        fontStyle: form.fontStyle,
        music: selectedTrack ? '__custom' : form.music,
        customPhotoUrl: form.customPhotoUrl,
        whatsappNumber: form.whatsappNumber,
        extras: form.extras,
      });
      addToCart({
        invitationId: res.data.id,
        templateName: tpl.name,
        templateImage: tpl.image,
        celebrant: form.celebrant,
        event: form.eventName,
        extras: form.extras,
        tier: tierId,
        tierPrice: tier?.price || 0,
        addedAt: new Date().toISOString(),
      });
      navigate('/carrello');
    } catch (e) {
      alert('Errore durante il salvataggio.');
    } finally {
      setSaving(false);
    }
  };

  const extraLabel = (id) => t(`${id}.label`);
  const dateStr = form.date ? new Date(form.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
      <div className="text-center mb-10">
        <div className="eyebrow mb-4">{t('builder.eyebrow')}</div>
        <h1 className="font-serif text-[2.4rem] md:text-[3.6rem] leading-[1.05] mb-3">
          {t('builder.title1')} <span className="italic-serif">{t('builder.title2')}</span>.
        </h1>
        <p className="text-[color:var(--ink-soft)]">{t('builder.subtitle')}</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-10 items-start">
        <div className="lg:col-span-3 space-y-6">
          {/* 1. TIER */}
          <div className="bg-white/60 border border-[color:var(--border)] rounded-2xl p-5">
            <h3 className="font-serif text-lg mb-1">1. {t('builder.step.tier')}</h3>
            <p className="text-xs text-[color:var(--ink-soft)] mb-4">{t('builder.step.tier.hint')}</p>
            <div className="relative">
              <select value={tierId} onChange={(e) => setTierId(e.target.value)} className="form-input appearance-none pr-10 font-medium">
                {pricingTiers.map((pt) => (
                  <option key={pt.id} value={pt.id}>{pt.name} — {price(pt.price)} — {t(`tier.${pt.id}.tagline`)}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--ink-soft)] pointer-events-none" />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-1.5">
              {tier?.features.map((_, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-[color:var(--ink-soft)]">
                  <Check size={12} className="text-[color:var(--burgundy)] flex-shrink-0" />
                  <span>{t(`tier.${tierId}.f${i + 1}`)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 2. TEMPLATE */}
          <div className="bg-white/60 border border-[color:var(--border)] rounded-2xl p-5">
            <h3 className="font-serif text-lg mb-3">2. {t('builder.step.template')}</h3>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {templates.map((tt) => (
                <button key={tt.id} onClick={() => pickTpl(tt)} className={`flex-shrink-0 w-20 h-28 rounded-xl overflow-hidden border-2 transition ${tpl.id === tt.id ? 'border-[color:var(--burgundy)] shadow-md' : 'border-transparent opacity-70 hover:opacity-100'}`}>
                  <img src={tt.image} alt={tt.name} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            <div className="mt-3 text-xs text-[color:var(--ink-soft)] italic"><b className="not-italic text-[color:var(--burgundy)]">{tpl.name}</b> · {tpl.category}</div>
          </div>

          {/* 3. EVENT DETAILS */}
          <div className="bg-white/60 border border-[color:var(--border)] rounded-2xl p-5">
            <h3 className="font-serif text-lg mb-4">3. {t('builder.step.details')}</h3>
            <div className="space-y-3">
              <div><label className="form-label">{t('builder.field.celebrant')}</label><input className="form-input" value={form.celebrant} onChange={(e) => setForm({ ...form, celebrant: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="form-label">{t('builder.field.age')}</label><input className="form-input" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} /></div>
                <div><label className="form-label">{t('builder.field.event')}</label><input className="form-input" value={form.eventName} onChange={(e) => setForm({ ...form, eventName: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="form-label">{t('builder.field.date')}</label><input type="date" className="form-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div><label className="form-label">{t('builder.field.time')}</label><input type="time" className="form-input" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></div>
              </div>
              <div><label className="form-label">{t('builder.field.location')}</label><input className="form-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
              <div><label className="form-label">{t('builder.field.message')}</label><textarea rows="2" className="form-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
              <div><label className="form-label">{t('builder.field.dress')}</label><input className="form-input" value={form.dress} onChange={(e) => setForm({ ...form, dress: e.target.value })} /></div>
            </div>
          </div>

          {/* 4. STYLE */}
          <div className="bg-white/60 border border-[color:var(--border)] rounded-2xl p-5">
            <h3 className="font-serif text-lg mb-4 flex items-center gap-2"><Palette size={16} className="text-[color:var(--burgundy)]" /> 4. {t('builder.step.style')}</h3>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div><div className="text-xs text-[color:var(--ink-soft)] mb-1">Sfondo</div><input type="color" className="w-full h-10 rounded cursor-pointer" value={palette.bg} onChange={(e) => setPalette({ ...palette, bg: e.target.value })} /></div>
              <div><div className="text-xs text-[color:var(--ink-soft)] mb-1">Accento</div><input type="color" className="w-full h-10 rounded cursor-pointer" value={palette.accent} onChange={(e) => setPalette({ ...palette, accent: e.target.value })} /></div>
              <div><div className="text-xs text-[color:var(--ink-soft)] mb-1">Testo</div><input type="color" className="w-full h-10 rounded cursor-pointer" value={palette.text} onChange={(e) => setPalette({ ...palette, text: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setForm({ ...form, fontStyle: 'serif' })} className={`p-3 rounded-xl border-2 transition ${form.fontStyle === 'serif' ? 'border-[color:var(--burgundy)]' : 'border-[color:var(--border)]'}`}><div className="font-serif text-lg">{t('builder.font.elegant')}</div></button>
              <button onClick={() => setForm({ ...form, fontStyle: 'sans' })} className={`p-3 rounded-xl border-2 transition ${form.fontStyle === 'sans' ? 'border-[color:var(--burgundy)]' : 'border-[color:var(--border)]'}`}><div style={{fontFamily: 'Inter, sans-serif'}} className="text-lg font-medium">{t('builder.font.modern')}</div></button>
            </div>
          </div>

          {hasPlus && (
            <div className="bg-white/60 border border-[color:var(--border)] rounded-2xl p-5">
              <h3 className="font-serif text-lg mb-4">{t('builder.step.music')}</h3>
              <MusicPicker value={selectedTrack} onChange={setSelectedTrack} />
            </div>
          )}

          {hasPlus && (
            <div className="bg-white/60 border border-[color:var(--border)] rounded-2xl p-5">
              <h3 className="font-serif text-lg mb-2">{t('builder.step.whatsapp')}</h3>
              <input className="form-input" placeholder="+39 333 1234567" value={form.whatsappNumber} onChange={(e) => setForm({ ...form, whatsappNumber: e.target.value })} />
            </div>
          )}

          {hasComplete && (
            <div className="bg-white/60 border border-[color:var(--border)] rounded-2xl p-5">
              <h3 className="font-serif text-lg mb-2">{t('builder.step.photo')}</h3>
              <p className="text-xs text-[color:var(--ink-soft)] mb-3">{t('builder.field.photoHint')}</p>
              <ImageUploader value={form.customPhotoUrl} onChange={(url) => setForm({ ...form, customPhotoUrl: url })} />
            </div>
          )}

          {/* Dynamic extra config boxes - appear ABOVE the extras list when selected */}
          {form.extras.includes('ex1') && (
            <div className="bg-white/60 border border-[color:var(--burgundy)]/40 rounded-2xl p-5">
              <h3 className="font-serif text-lg mb-2">🎵 Musica di sottofondo custom <span className="text-xs text-[color:var(--burgundy)]">(extra)</span></h3>
              <p className="text-xs text-[color:var(--ink-soft)] mb-3">URL diretto del file MP3 (o carica poi tramite supporto).</p>
              <input className="form-input" placeholder="https://... (URL file MP3)" value={form.extraCustomMusicUrl} onChange={(e) => setForm({ ...form, extraCustomMusicUrl: e.target.value })} />
            </div>
          )}
          {form.extras.includes('ex2') && !hasComplete && (
            <div className="bg-white/60 border border-[color:var(--burgundy)]/40 rounded-2xl p-5">
              <h3 className="font-serif text-lg mb-2">🖼️ {t('builder.step.photo')} <span className="text-xs text-[color:var(--burgundy)]">(extra)</span></h3>
              <ImageUploader value={form.customPhotoUrl} onChange={(url) => setForm({ ...form, customPhotoUrl: url })} />
            </div>
          )}
          {form.extras.includes('ex3') && (
            <div className="bg-white/60 border border-[color:var(--burgundy)]/40 rounded-2xl p-5">
              <h3 className="font-serif text-lg mb-2">⏳ Countdown all'evento <span className="text-xs text-[color:var(--burgundy)]">(extra)</span></h3>
              <p className="text-xs text-[color:var(--ink-soft)]">Il countdown verrà calcolato automaticamente dalla data dell'evento: <b className="text-[color:var(--burgundy)]">{form.date} ore {form.time}</b></p>
            </div>
          )}
          {form.extras.includes('ex4') && (
            <div className="bg-white/60 border border-[color:var(--burgundy)]/40 rounded-2xl p-5">
              <h3 className="font-serif text-lg mb-2">📍 Mappa interattiva del luogo <span className="text-xs text-[color:var(--burgundy)]">(extra)</span></h3>
              <p className="text-xs text-[color:var(--ink-soft)] mb-2">La mappa userà l'indirizzo del luogo evento:</p>
              <input className="form-input" placeholder="Indirizzo completo per la mappa" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </div>
          )}
          {form.extras.includes('ex5') && (
            <div className="bg-white/60 border border-[color:var(--burgundy)]/40 rounded-2xl p-5">
              <h3 className="font-serif text-lg mb-2">📷 Galleria foto extra <span className="text-xs text-[color:var(--burgundy)]">(extra)</span></h3>
              <p className="text-xs text-[color:var(--ink-soft)] mb-3">Carica foto aggiuntive per la galleria (max 5MB l'una).</p>
              <ImageUploader value={form.extraGalleryUrls[0] || ''} onChange={(url) => setForm({ ...form, extraGalleryUrls: [url] })} label="Aggiungi foto galleria" />
            </div>
          )}
          {form.extras.includes('ex6') && !hasPlus && (
            <div className="bg-white/60 border border-[color:var(--burgundy)]/40 rounded-2xl p-5">
              <h3 className="font-serif text-lg mb-2">📱 {t('builder.step.whatsapp')} <span className="text-xs text-[color:var(--burgundy)]">(extra)</span></h3>
              <input className="form-input" placeholder="+39 333 1234567" value={form.whatsappNumber} onChange={(e) => setForm({ ...form, whatsappNumber: e.target.value })} />
            </div>
          )}
          {form.extras.includes('ex7') && (
            <div className="bg-white/60 border border-[color:var(--burgundy)]/40 rounded-2xl p-5">
            </div>
          )}
          {form.extras.includes('ex8') && (
            <div className="bg-white/60 border border-[color:var(--burgundy)]/40 rounded-2xl p-5">
              <h3 className="font-serif text-lg mb-2">🎬 Video di benvenuto <span className="text-xs text-[color:var(--burgundy)]">(extra)</span></h3>
              <p className="text-xs text-[color:var(--ink-soft)] mb-3">URL diretto del video (YouTube, Vimeo o file MP4).</p>
              <input className="form-input" placeholder="https://youtu.be/... o URL diretto MP4" value={form.extraVideoUrl} onChange={(e) => setForm({ ...form, extraVideoUrl: e.target.value })} />
            </div>
          )}
          {form.extras.includes('ex9') && (
            <div className="bg-white/60 border border-[color:var(--burgundy)]/40 rounded-2xl p-5">
              <h3 className="font-serif text-lg mb-2">🍽️ Menu della cena <span className="text-xs text-[color:var(--burgundy)]">(extra)</span></h3>
              <textarea rows="4" className="form-input" placeholder="Antipasto: ...&#10;Primo: ...&#10;Secondo: ...&#10;Dolce: ..." value={form.extraMenu} onChange={(e) => setForm({ ...form, extraMenu: e.target.value })} />
            </div>
          )}
          {form.extras.includes('ex10') && (
            <div className="bg-white/60 border border-[color:var(--burgundy)]/40 rounded-2xl p-5">
              <h3 className="font-serif text-lg mb-2">👗 Dress code & tema <span className="text-xs text-[color:var(--burgundy)]">(extra)</span></h3>
              <input className="form-input" placeholder="es. Elegante · tonalità pastello · maschere" value={form.dress} onChange={(e) => setForm({ ...form, dress: e.target.value })} />
            </div>
          )}
          {form.extras.includes('ex11') && (
            <div className="bg-white/60 border border-[color:var(--burgundy)]/40 rounded-2xl p-5">
              <h3 className="font-serif text-lg mb-2">🎁 Lista regali / registry <span className="text-xs text-[color:var(--burgundy)]">(extra)</span></h3>
              <input className="form-input" placeholder="URL Amazon Wishlist o simile" value={form.extraRegistryUrl} onChange={(e) => setForm({ ...form, extraRegistryUrl: e.target.value })} />
            </div>
          )}
          {form.extras.includes('ex12') && (
            <div className="bg-white/60 border border-[color:var(--burgundy)]/40 rounded-2xl p-5">
              <h3 className="font-serif text-lg mb-2">📱 QR code stampabile <span className="text-xs text-[color:var(--burgundy)]">(extra)</span></h3>
              <p className="text-xs text-[color:var(--ink-soft)]">Il QR code verrà generato automaticamente con il link del tuo invito, pronto per essere stampato su inviti cartacei, biglietti o banner.</p>
            </div>
          )}

          <div className="bg-white/60 border border-[color:var(--border)] rounded-2xl p-5">
            <h3 className="font-serif text-lg mb-1">{t('builder.step.extras')}</h3>
            <p className="text-xs text-[color:var(--ink-soft)] mb-4">{t('builder.step.extras.hint')}</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {extraOptions.map((o) => {
                const active = form.extras.includes(o.id);
                return (
                  <button key={o.id} onClick={() => toggleExtra(o.id)} className={`flex items-center justify-between p-3 rounded-lg border text-left text-sm transition ${active ? 'bg-[color:var(--burgundy)]/8 border-[color:var(--burgundy)]' : 'border-[color:var(--border)] hover:border-[color:var(--burgundy)]/50'}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${active ? 'bg-[color:var(--burgundy)] border-[color:var(--burgundy)]' : 'border-[color:var(--border)]'}`}>
                        {active && <Check size={10} className="text-[#f8ede0]" />}
                      </div>
                      <span>{extraLabel(o.id)}</span>
                    </div>
                    <span className="text-[color:var(--burgundy)] text-xs">+{price(o.price)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* SUMMARY at bottom - NOT sticky */}
          <div className="bg-[color:var(--burgundy)] rounded-2xl p-6 text-[#f8ede0]">
            <h3 className="font-serif text-xl mb-1">{t('builder.summary.title')}</h3>
            <p className="text-xs opacity-80 mb-4">{t('builder.summary.subtitle')}</p>

            <div className="space-y-1 text-sm mb-4 pb-4 border-b border-white/20">
              <div className="flex justify-between">
                <span>{t('builder.summary.plan')} {tier?.name}</span>
                <span>{price(tier?.price || 0)}</span>
              </div>
              {form.extras.map((exId) => (
                <div key={exId} className="flex justify-between opacity-80 text-xs">
                  <span>+ {extraLabel(exId)}</span>
                  <span>{price(0.5)}</span>
                </div>
              ))}
            </div>

            <div className="flex items-baseline justify-between mb-4">
              <span className="text-sm opacity-80">{t('builder.summary.total')}</span>
              <span className="font-serif text-3xl">{price(total)}</span>
            </div>

            <button onClick={handleSaveAndCart} disabled={saving} className="w-full px-6 py-3 rounded-full bg-[#f8ede0] text-[color:var(--burgundy)] inline-flex items-center justify-center gap-2 disabled:opacity-60 font-medium">
              {saving ? <><Loader2 size={16} className="animate-spin" /> {t('builder.saving')}</> : <><Save size={16} /> {t('builder.save')}</>}
            </button>
          </div>
        </div>

        {/* RIGHT: STATIC preview (smaller) + info list below */}
        <div className="lg:col-span-2 lg:sticky lg:top-24">
          <div className="flex flex-col items-center">
            <div className="eyebrow mb-3 text-center">{t('builder.preview.live')}</div>
            <StaticPreview data={previewData} size="sm" />
            <div className="text-center mt-3 text-sm italic text-[color:var(--ink-soft)]">{tpl.name}</div>

            {/* Info list below phone */}
            <div className="w-full max-w-[260px] mt-6 bg-white/60 border border-[color:var(--border)] rounded-xl p-4 text-sm space-y-2">
              <div className="flex items-start gap-2"><User size={13} className="text-[color:var(--burgundy)] mt-0.5 flex-shrink-0" /><div><b>{form.celebrant}</b>{form.age && <span className="text-[color:var(--ink-soft)] italic"> · {form.age}</span>}</div></div>
              <div className="flex items-start gap-2"><Calendar size={13} className="text-[color:var(--burgundy)] mt-0.5 flex-shrink-0" /><div>{dateStr}</div></div>
              <div className="flex items-start gap-2"><Clock size={13} className="text-[color:var(--burgundy)] mt-0.5 flex-shrink-0" /><div>ore {form.time}</div></div>
              <div className="flex items-start gap-2"><MapPin size={13} className="text-[color:var(--burgundy)] mt-0.5 flex-shrink-0" /><div>{form.location}</div></div>
              {selectedTrack && <div className="flex items-start gap-2"><Music size={13} className="text-[color:var(--burgundy)] mt-0.5 flex-shrink-0" /><div className="text-xs">{selectedTrack.title}<br /><span className="text-[color:var(--ink-soft)] italic">{selectedTrack.artist}</span></div></div>}
              {hasPlus && form.whatsappNumber && <div className="flex items-start gap-2"><MessageCircle size={13} className="text-[color:var(--burgundy)] mt-0.5 flex-shrink-0" /><div className="text-xs">RSVP {form.whatsappNumber}</div></div>}
              {form.customPhotoUrl && <div className="flex items-start gap-2"><ImageIcon size={13} className="text-[color:var(--burgundy)] mt-0.5 flex-shrink-0" /><div className="text-xs">Foto personalizzata</div></div>}
              <div className="flex items-start gap-2"><TypeIcon size={13} className="text-[color:var(--burgundy)] mt-0.5 flex-shrink-0" /><div className="text-xs">Font {form.fontStyle === 'serif' ? 'Elegante' : 'Moderno'}</div></div>

              {/* Extras list */}
              {form.extras.length > 0 && (
                <div className="pt-2 mt-2 border-t border-[color:var(--border)]">
                  <div className="text-[10px] text-[color:var(--burgundy)] tracking-widest mb-1.5">EXTRA AGGIUNTI</div>
                  <ul className="space-y-1">
                    {form.extras.map((exId) => (
                      <li key={exId} className="text-xs text-[color:var(--ink-soft)] flex items-start gap-1">
                        <Check size={10} className="text-[color:var(--burgundy)] mt-0.5 flex-shrink-0" />
                        <span>{extraLabel(exId)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
